import DOMPurify from 'dompurify';

/**
 * Validates if a string is a valid base64-encoded image
 * @param base64String - The base64 string to validate (with or without data URI prefix)
 * @returns true if valid, false otherwise
 */
export const isValidBase64Image = (base64String: string | undefined | null): boolean => {
  if (!base64String) return false;

  try {
    // Check if it has data URI prefix
    const dataUriRegex = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/i;
    
    let base64Data: string;
    
    if (dataUriRegex.test(base64String)) {
      // Extract base64 part after the prefix
      base64Data = base64String.split(',')[1];
    } else {
      base64Data = base64String;
    }

    // Validate base64 format (only valid base64 characters)
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(base64Data)) {
      return false;
    }

    // Additional length check (base64 should be divisible by 4 when padded)
    if (base64Data.length % 4 !== 0) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Base64 validation error:', error);
    return false;
  }
};

/**
 * Safely constructs a data URI for an image, validating the base64 content
 * @param base64Data - The base64 string (with or without data URI prefix)
 * @param defaultImage - Optional default image to return if validation fails
 * @returns Safe data URI or empty string
 */
export const getSafeImageDataUri = (
  base64Data: string | undefined | null,
  defaultImage: string = ''
): string => {
  if (!isValidBase64Image(base64Data)) {
    console.warn('Invalid base64 image data detected');
    return defaultImage;
  }

  // If already has data URI prefix, return as is (after validation)
  if (base64Data && base64Data.startsWith('data:image/')) {
    return base64Data;
  }

  // Add data URI prefix
  return `data:image/png;base64,${base64Data}`;
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (dirty: string | undefined | null): string => {
  if (!dirty) return '';
  
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br'],
    ALLOWED_ATTR: []
  });
};

/**
 * Sanitizes text content (strips all HTML)
 * @param text - The potentially unsafe text
 * @returns Plain text with HTML entities escaped
 */
export const sanitizeText = (text: string | undefined | null): string => {
  if (!text) return '';
  
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  });
};

/**
 * Safely encodes URL parameters
 * @param param - The parameter value to encode
 * @returns URL-encoded string
 */
export const encodeUrlParam = (param: string | number | undefined | null): string => {
  if (param === undefined || param === null) return '';
  return encodeURIComponent(String(param));
};

/**
 * Validates and sanitizes a filename
 * @param filename - The filename to validate
 * @returns Safe filename or default
 */
export const sanitizeFilename = (filename: string | undefined | null, defaultName: string = 'file'): string => {
  if (!filename) return defaultName;
  
  // Remove path traversal attempts and special characters
  const sanitized = filename
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .replace(/\.\./g, '')
    .trim();
  
  return sanitized || defaultName;
};
